import React from 'react';
import DatePicker from '../DatePicker.jsx';
import { capitalize } from '../../utils/appHelpers.jsx';

export function TariffEditModal({ tariff, onClose, onSave, segments = [], groups = [], categories = [], activeEvent = 'none' }) {
  const [form, setForm] = React.useState(() => ({
    id: tariff.id,
    title: tariff.title || "",
    category: tariff.category || "",
    segment: tariff.segment || "",
    group: tariff.group || "",
    dataLabel: tariff.dataLabel ?? "",
    price: tariff.price ?? "",
    promoPrice: tariff.promoPrice ?? "",
    connectionFee: tariff.connectionFee ?? "",
    termMonths: tariff.termMonths ?? 24,
    startsOn: tariff.startsOn || "",
    expiresOn: tariff.expiresOn || "",
    showValidityLeft: tariff.showValidityLeft || false,
    showPromoAction: tariff.group === 'promo' ? (tariff.showPromoAction !== false) : (tariff.showPromoAction || false),
    discountEuro: tariff.discountEuro ?? "",
    promoLabel: tariff.promoLabel || "",
    featuresText: Array.isArray(tariff.features) ? tariff.features.join("\n") : "",
    showPromoTag: false,
    showExpiry: false,
    cardStyle: tariff.cardStyle || "standard",
    iconStyle: tariff.iconStyle || "outline",
    colorScheme: tariff.colorScheme || "cyan",
    customColor: tariff.customColor || "#00d4ff",
    customIcon: tariff.customIcon || "",
  }));

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  
  // Custom Color Picker State
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const [colorInput, setColorInput] = React.useState(form.customColor);
  // DatePicker popup state for the starts/expires fields
  const [showStartsPicker, setShowStartsPicker] = React.useState(false);
  const [showExpiresPicker, setShowExpiresPicker] = React.useState(false);
  
  // Icon-Auswahl erweitert
  const availableIcons = [
    { id: 'phone', icon: '📱', label: 'Handy' },
    { id: 'wifi', icon: '📶', label: 'WiFi' },
    { id: 'rocket', icon: '🚀', label: 'Rakete' },
    { id: 'star', icon: '⭐', label: 'Stern' },
    { id: 'lightning', icon: '⚡', label: 'Blitz' },
    { id: 'fire', icon: '🔥', label: 'Feuer' },
    { id: 'diamond', icon: '💎', label: 'Diamant' },
    { id: 'crown', icon: '👑', label: 'Krone' },
    { id: 'gift', icon: '🎁', label: 'Geschenk' },
    { id: 'globe', icon: '🌍', label: 'Globus' },
    { id: 'cloud', icon: '☁️', label: 'Cloud' },
    { id: 'shield', icon: '🛡️', label: 'Schild' },
    { id: 'heart', icon: '❤️', label: 'Herz' },
    { id: 'infinity', icon: '♾️', label: 'Unbegrenzt' },
    { id: 'zap', icon: '💫', label: 'Magie' },
    { id: 'target', icon: '🎯', label: 'Ziel' },
  ];

  // Iconauswahl-States
  const [categoryNewMode, setCategoryNewMode] = React.useState(!categories.includes(form.category) && !!form.category);
  const [categoryNew, setCategoryNew] = React.useState(!categories.includes(form.category) ? (form.category || "") : "");

  // Features-Liste separat pflegen
  const [featuresList, setFeaturesList] = React.useState(() => Array.isArray(tariff.features) ? [...tariff.features] : []);
  React.useEffect(() => {
    setForm((f) => ({ ...f, featuresText: featuresList.join("\n") }));
  }, [featuresList]);

  // Helfer für Feature-Chips
  const toggleFeature = (key) => {
    setFeaturesList((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };
  const addFeature = (txt) => {
    const v = String(txt ?? "").trim();
    if (!v) return;
    setFeaturesList((prev) => (prev.includes(v) ? prev : [...prev, v]));
  };
  const removeFeature = (key) => {
    setFeaturesList((prev) => prev.filter((x) => x !== key));
  };

  // Predefined Feature-Liste (Icons einfach gehalten)
  const iconSmall = "w-3.5 h-3.5";
  const predefined = [
    { key: "Allnet-Flat", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 8h20"/><path d="M2 16h20"/></svg>) },
    { key: "EU-Roaming", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/></svg>) },
    { key: "inklusive 5G", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18"/><path d="M7 12v6"/><path d="M11 12v10"/></svg>) },
    { key: "bis 600 Mbit/s", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18"/></svg>) },
    { key: "bis 300 Mbit/s", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18"/></svg>) },
    { key: "bis 15 Mbit/s", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18"/></svg>) },
    { key: "bis 10 Mbit/s", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18"/></svg>) },
    { key: "Grow-Vorteil", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 20h18"/><path d="M7 20V8"/></svg>) },
    { key: "Connect-Option", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="8" cy="12" r="3"/><circle cx="16" cy="12" r="3"/><path d="M11 12h2"/></svg>) },
    { key: "Weitersurf-Garantie", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 13a8 8 0 1 0 8-8"/></svg>) },
    { key: "Junge Leute Rabatt", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="8" cy="8" r="3"/><circle cx="16" cy="8" r="3"/></svg>) },
    { key: "60 Plus Vorteil", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/></svg>) },
    { key: "Festnetznummer", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/></svg>) },
    { key: "Rufnummernmitnahme", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>) },
    { key: "30 Tage testen", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/></svg>) },
    { key: "Kombivorteil", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="8" cy="12" r="3"/><circle cx="16" cy="12" r="3"/><path d="M5 12h14"/></svg>) },
    { key: "o2 Cloud", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 17.5A4.5 4.5 0 0 0 15.5 13h-1A6.5 6.5 0 1 0 8 20h11"/></svg>) },
    { key: "Priority Service", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><path d="m5 13 4 4L19 7"/></svg>) },
    { key: "Watch-Option", icon: () => (<svg viewBox="0 0 24 24" className={iconSmall} fill="none" stroke="currentColor" strokeWidth="2"><rect x="7" y="5" width="10" height="14" rx="2"/></svg>) },
  ];

  // Promo/Expiry Logik (einfacher Stand)
  const promoDisabled = !!Number(form.promoPrice);
  const expiryDisabled = true;

  const handleSubmit = (e) => {
    e.preventDefault();
    const toNumber = (v) => {
      if (v === "" || v == null) return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const features = featuresList;

    // dataLabel kann Zahl oder Text sein
    let dataLabel = form.dataLabel;
    if (typeof dataLabel === "string" && dataLabel.trim() !== "") {
      const asNum = Number(dataLabel.replace(",", "."));
      if (Number.isFinite(asNum)) dataLabel = asNum;
    }

    onSave({
      id: form.id,
      title: form.title.trim(),
      category: (categoryNewMode ? categoryNew : form.category).trim(),
      segment: form.segment.trim(),
      group: form.group.trim(),
      dataLabel,
      price: toNumber(form.price),
      promoPrice: toNumber(form.promoPrice),
      connectionFee: toNumber(form.connectionFee),
      termMonths: toNumber(form.termMonths) ?? 24,
      startsOn: form.startsOn.trim() || undefined,
      expiresOn: form.expiresOn.trim() || undefined,
      showValidityLeft: form.showValidityLeft || false,
      showPromoAction: form.showPromoAction || false,
      discountEuro: form.discountEuro ? parseFloat(form.discountEuro) : undefined,
      promoLabel: form.promoLabel.trim() || undefined,
      features,
      cardStyle: form.cardStyle,
      iconStyle: form.iconStyle,
      colorScheme: form.colorScheme,
    });
  };

  // Icons
  const iconCls = "w-4.5 h-4.5";
  const SegmentIcon = (s) => {
    switch (s) {
      case "mobile":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="2" width="10" height="20" rx="2"/><line x1="11" y1="5" x2="13" y2="5"/></svg>);
      case "blue":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>);
      case "prepaid":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18"/></svg>);
      case "myhome":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1"/></svg>);
      default:
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/></svg>);
    }
  };

  const GroupIcon = (g) => {
    switch (g) {
      case "promo":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>);
      case "classic":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>);
      case "young":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="8" cy="8" r="3"/><circle cx="16" cy="8" r="3"/><path d="M4 20c0-3 2-5 4-5s4 2 4 5"/><path d="M12 20c0-3 2-5 4-5s4 2 4 5"/></svg>);
      case "60plus":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M9 10.5a2 2 0 1 0 0 3"/><path d="M15 11.5v4"/><path d="M13 13.5h4"/></svg>);
      case "business":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="10" width="7" height="11" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>);
      default:
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/></svg>);
    }
  };

  const CategoryIcon = (c) => {
    switch (c) {
      case "Junge Leute":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="8" cy="8" r="3"/><circle cx="16" cy="8" r="3"/><path d="M4 20c0-3 2-5 4-5s4 2 4 5"/><path d="M12 20c0-3 2-5 4-5s4 2 4 5"/></svg>);
      case "60+ Vorteil":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M9 10.5a2 2 0 1 0 0 3"/><path d="M15 11.5v4"/><path d="M13 13.5h4"/></svg>);
      case "SOHO":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="10" width="7" height="11" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>);
      case "O2 Blue":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>);
      case "Internet & Festnetz":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/></svg>);
      case "Promo Aktion":
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>);
      default:
        return (<svg viewBox="0 0 24 24" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="5" y="5" width="14" height="8" rx="2"/></svg>);
    }
  };

  const IconOption = ({ active, onClick, title, children }) => (
    <button 
      type="button" 
      title={title} 
      onClick={onClick} 
      className={`edit-lightbox-icon-btn ${active ? 'active' : ''}`}
    >
      <span className="icon-wrapper">{children}</span>
      <span className="icon-label">{title}</span>
    </button>
  );

  // Tab state for lightbox navigation
  const [activeTab, setActiveTab] = React.useState('basis');

  // Bubbles für Modal
  const modalBubbles = React.useMemo(() => {
    return Array.from({ length: 15 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: 6 + Math.random() * 14,
      delay: Math.random() * 12,
      duration: 14 + Math.random() * 12
    }));
  }, []);

  // Schneeflocken für Winter/Weihnachten
  const modalSnowflakes = React.useMemo(() => {
    return Array.from({ length: 25 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: 8 + Math.random() * 14,
      delay: Math.random() * 10,
      duration: 6 + Math.random() * 8,
      symbol: ['❄', '❅', '❆', '✦', '✧'][Math.floor(Math.random() * 5)]
    }));
  }, []);

  // Konfetti für Silvester/Neujahr
  const modalKonfetti = React.useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: 6 + Math.random() * 10,
      delay: Math.random() * 8,
      duration: 5 + Math.random() * 6,
      shape: ['square', 'circle', 'star'][Math.floor(Math.random() * 3)]
    }));
  }, []);

  // Prüfen ob Schnee oder Konfetti angezeigt werden soll
  const showSnow = activeEvent === 'christmas' || activeEvent === 'christmas2' || activeEvent === 'winter';
  const showKonfetti = activeEvent === 'silvester' || activeEvent === 'newyear';

  return (
    <div className="edit-lightbox-overlay" onClick={onClose}>
      <div className={`edit-lightbox-content premium-modal event-${activeEvent}`} onClick={(e) => e.stopPropagation()}>
        {/* Animated Liquid Background */}
        <div className="modal-liquid-bg">
          <div className="liquid-blob liquid-blob-1"></div>
          <div className="liquid-blob liquid-blob-2"></div>
          <div className="liquid-blob liquid-blob-3"></div>
          {/* Aufsteigende Bubbles */}
          {modalBubbles.map(bubble => (
            <div
              key={bubble.id}
              className="modal-bubble"
              style={{
                left: `${bubble.left}%`,
                width: `${bubble.size}px`,
                height: `${bubble.size}px`,
                '--bubble-delay': `${bubble.delay}s`,
                '--bubble-duration': `${bubble.duration}s`
              }}
            />
          ))}
        </div>

        {/* Schneefall für Winter/Weihnachten */}
        {showSnow && (
          <div className="modal-snow-container">
            {modalSnowflakes.map(flake => (
              <div
                key={flake.id}
                className="modal-snowflake"
                style={{
                  left: `${flake.left}%`,
                  '--snow-size': `${flake.size}px`,
                  '--snow-delay': `${flake.delay}s`,
                  '--snow-duration': `${flake.duration}s`
                }}
              >
                {flake.symbol}
              </div>
            ))}
          </div>
        )}

        {/* Konfetti für Silvester/Neujahr */}
        {showKonfetti && (
          <div className="modal-konfetti-container">
            {modalKonfetti.map(k => (
              <div
                key={k.id}
                className={`modal-konfetti ${k.shape}`}
                style={{
                  left: `${k.left}%`,
                  '--konfetti-size': `${k.size}px`,
                  '--konfetti-delay': `${k.delay}s`,
                  '--konfetti-duration': `${k.duration}s`
                }}
              />
            ))}
          </div>
        )}
        
        {/* Animated Border Glow */}
        <div className="modal-border-glow"></div>
        
        {/* Header mit Premium Design */}
        <div className="edit-lightbox-header premium-header">
          <div className="header-glow-line"></div>
          <h2 className="edit-lightbox-title premium-title">
            <div className="title-icon-wrapper compact">
              <svg viewBox="0 0 24 24" fill="none" stroke="url(#titleGradient)" strokeWidth="2">
                <defs>
                  <linearGradient id="titleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#00d4ff"/>
                    <stop offset="50%" stopColor="#a855f7"/>
                    <stop offset="100%" stopColor="#00d4ff"/>
                  </linearGradient>
                </defs>
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
            <span className="title-text compact">Tarif Designer</span>
            <span className="title-badge compact">PRO</span>
          </h2>
          <button className="edit-lightbox-close premium-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Premium Tab Navigation */}
        <div className="edit-lightbox-tabs premium-tabs">
          <div className="tabs-glow-track"></div>
          <button 
            className={`edit-lightbox-tab premium-tab ${activeTab === 'basis' ? 'active' : ''}`}
            onClick={() => setActiveTab('basis')}
          >
            <div className="tab-icon-glow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <span className="tab-label">Basis</span>
            <span className="tab-indicator"></span>
          </button>
          <button 
            className={`edit-lightbox-tab premium-tab ${activeTab === 'kategorien' ? 'active' : ''}`}
            onClick={() => setActiveTab('kategorien')}
          >
            <div className="tab-icon-glow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 3H3v7h7V3z"/>
                <path d="M21 3h-7v7h7V3z"/>
                <path d="M21 14h-7v7h7v-7z"/>
                <path d="M10 14H3v7h7v-7z"/>
              </svg>
            </div>
            <span className="tab-label">Kategorien</span>
            <span className="tab-indicator"></span>
          </button>
          <button 
            className={`edit-lightbox-tab premium-tab ${activeTab === 'features' ? 'active' : ''}`}
            onClick={() => setActiveTab('features')}
          >
            <div className="tab-icon-glow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <span className="tab-label">Features</span>
            <span className="tab-indicator"></span>
          </button>
          <button 
            className={`edit-lightbox-tab premium-tab ${activeTab === 'design' ? 'active' : ''}`}
            onClick={() => setActiveTab('design')}
          >
            <div className="tab-icon-glow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="13.5" cy="6.5" r="0.5"/>
                <circle cx="17.5" cy="10.5" r="0.5"/>
                <circle cx="8.5" cy="7.5" r="0.5"/>
                <circle cx="6.5" cy="12.5" r="0.5"/>
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z"/>
              </svg>
            </div>
            <span className="tab-label">Design</span>
            <span className="tab-indicator"></span>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="edit-lightbox-body premium-body">
            
            {/* Tab: Basis */}
            {activeTab === 'basis' && (
              <div className="edit-lightbox-tab-content premium-content">
                <div className="premium-section-header compact">
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#sectionGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="sectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#00d4ff"/>
                        <stop offset="100%" stopColor="#a855f7"/>
                      </linearGradient>
                    </defs>
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <h3>Grunddaten</h3>
                  <div className="section-line"></div>
                </div>
                
                <div className="premium-input-container floating">
                  <div className="input-icon-wrapper">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 7V4a2 2 0 0 1 2-2h8.5L20 7.5V20a2 2 0 0 1-2 2h-7"/>
                      <path d="M14 2v6h6"/>
                      <path d="M5 12H3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2z"/>
                      <path d="M5 19v-4"/>
                    </svg>
                  </div>
                  <input 
                    type="text" 
                    id="title-input"
                    value={form.title} 
                    onChange={(e) => update("title", e.target.value)} 
                    placeholder=" "
                    className="premium-input"
                  />
                  <label htmlFor="title-input" className="premium-label">Tarifname</label>
                  <div className="input-glow"></div>
                </div>

                <div className="premium-row">
                  <div className="premium-input-container floating">
                    <div className="input-icon-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M16 8h-6a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8"/>
                        <path d="M12 6v2m0 8v2"/>
                      </svg>
                    </div>
                    <input 
                      type="number" 
                      id="price-input"
                      step="0.01" 
                      value={form.price} 
                      onChange={(e) => update("price", e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label htmlFor="price-input" className="premium-label">Preis €/Monat</label>
                    <div className="input-glow"></div>
                  </div>
                  <div className="premium-input-container floating promo-input">
                    <div className="input-icon-wrapper promo-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                        <line x1="7" y1="7" x2="7.01" y2="7"/>
                      </svg>
                    </div>
                    <input 
                      type="number" 
                      id="promo-input"
                      step="0.01" 
                      value={form.promoPrice} 
                      onChange={(e) => update("promoPrice", e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label htmlFor="promo-input" className="premium-label">Aktionspreis €</label>
                    <div className="input-glow promo-glow"></div>
                  </div>
                </div>

                <div className="premium-row triple">
                  <div className="premium-input-container floating">
                    <div className="input-icon-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                      </svg>
                    </div>
                    <input 
                      type="number" 
                      id="connection-input"
                      step="0.01" 
                      value={form.connectionFee} 
                      onChange={(e) => update("connectionFee", e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label htmlFor="connection-input" className="premium-label">Anschluss €</label>
                    <div className="input-glow"></div>
                  </div>
                  <div className="premium-input-container floating">
                    <div className="input-icon-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </div>
                    <input 
                      type="number" 
                      id="term-input"
                      value={form.termMonths} 
                      onChange={(e) => update("termMonths", e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label htmlFor="term-input" className="premium-label">Laufzeit (Monate)</label>
                    <div className="input-glow"></div>
                  </div>
                  <div className="premium-input-container floating">
                    <div className="input-icon-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </div>
                    <input 
                      type="text" 
                      id="data-input"
                      value={form.dataLabel} 
                      onChange={(e) => update("dataLabel", e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label htmlFor="data-input" className="premium-label">Datenvolumen</label>
                    <div className="input-glow"></div>
                  </div>
                </div>

                <div className="premium-section-header compact" style={{marginTop: '12px'}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#promoGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="promoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f59e0b"/>
                        <stop offset="100%" stopColor="#ec4899"/>
                      </linearGradient>
                    </defs>
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                  </svg>
                  <h3>Promo-Optionen</h3>
                  <div className="section-line promo"></div>
                </div>

                <div className="premium-row">
                  <div className="premium-input-container floating" style={{ position: 'relative' }}>
                    <div className="input-icon-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                    </div>
                    <input 
                      type="text" 
                      id="starts-input"
                      value={form.startsOn} 
                      onChange={(e) => update("startsOn", e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label htmlFor="starts-input" className="premium-label">Gültig ab (z.B. 03.12.2024)</label>
                    <div className="input-glow"></div>
                    <div style={{ position: 'absolute', right: 8, top: 10 }}>
                      <button className="btn btn-ghost btn-square btn-sm" onClick={() => setShowStartsPicker(s => !s)} aria-label="Kalender öffnen">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </button>
                    </div>
                    {showStartsPicker && (
                      <DatePicker value={form.startsOn} onSelect={(d) => { update('startsOn', d); }} onClose={() => setShowStartsPicker(false)} />
                    )}
                  </div>
                  <div className="premium-input-container floating" style={{ position: 'relative' }}>
                    <div className="input-icon-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                    </div>
                    <input 
                      type="text" 
                      id="expires-input"
                      value={form.expiresOn} 
                      onChange={(e) => update("expiresOn", e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label htmlFor="expires-input" className="premium-label">Gültig bis (z.B. 02.03.2025)</label>
                    <div className="input-glow"></div>
                    <div style={{ position: 'absolute', right: 8, top: 10 }}>
                      <button className="btn btn-ghost btn-square btn-sm" onClick={() => setShowExpiresPicker(s => !s)} aria-label="Kalender öffnen">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </button>
                    </div>
                    {showExpiresPicker && (
                      <DatePicker value={form.expiresOn} onSelect={(d) => { update('expiresOn', d); }} onClose={() => setShowExpiresPicker(false)} />
                    )}
                  </div>
                </div>
                
                {/* Checkbox für Badge rechts anzeigen */}
                <div className="premium-row mt-4">
                  <label className="premium-toggle-container compact">
                    <input
                      type="checkbox"
                      checked={form.showValidityLeft}
                      onChange={(e) => update("showValidityLeft", e.target.checked)}
                      className="premium-toggle-input"
                    />
                    <span className="premium-toggle-slider"></span>
                    <span className="premium-toggle-label">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                        <path d="M21 3l-6 6m0-6l6 6"/>
                        <path d="M3 21l6-6m0 6l-6-6"/>
                        <rect x="8" y="8" width="8" height="8" rx="1"/>
                      </svg>
                      Badge rechts anzeigen
                    </span>
                  </label>
                </div>

                {/* Promo-Aktion Toggle - Standard an bei Promo-Tarifen */}
                <div className="premium-row">
                  <label className="premium-toggle-container compact">
                    <input
                      type="checkbox"
                      checked={form.showPromoAction}
                      onChange={(e) => update("showPromoAction", e.target.checked)}
                      className="premium-toggle-input"
                    />
                    <span className="premium-toggle-slider"></span>
                    <span className="premium-toggle-label">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                      </svg>
                      Promo-Aktion anzeigen {form.group === 'promo' ? '(Standard)' : ''}
                    </span>
                  </label>
                </div>

                {/* Rabatt in Euro - wird automatisch in % umgerechnet */}
                <div className="premium-row">
                  <div className="premium-input-container floating compact">
                    <div className="input-icon-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="19" y1="5" x2="5" y2="19"/>
                        <circle cx="6.5" cy="6.5" r="2.5"/>
                        <circle cx="17.5" cy="17.5" r="2.5"/>
                      </svg>
                    </div>
                    <input 
                      type="text" 
                      id="discount-euro-input"
                      value={form.discountEuro} 
                      onChange={(e) => update("discountEuro", e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label htmlFor="discount-euro-input" className="premium-label">Rabatt in € (wird in % angezeigt)</label>
                    <div className="input-glow"></div>
                    {form.discountEuro && form.price ? (
                      <div className="discount-preview">
                        = {((parseFloat(form.discountEuro) / parseFloat(form.price)) * 100).toFixed(0)}% Rabatt
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="premium-row">
                  <div className="premium-input-container floating">
                    <div className="input-icon-wrapper promo-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                        <line x1="7" y1="7" x2="7.01" y2="7"/>
                      </svg>
                    </div>
                    <input 
                      type="text" 
                      id="label-input"
                      value={form.promoLabel} 
                      onChange={(e) => update("promoLabel", e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label htmlFor="label-input" className="premium-label">Promo-Label (z.B. AKTION)</label>
                    <div className="input-glow promo-glow"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Kategorien */}
            {activeTab === 'kategorien' && (
              <div className="edit-lightbox-tab-content premium-content">
                <div className="premium-section-header compact">
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#catGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="catGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#00d4ff"/>
                        <stop offset="100%" stopColor="#10b981"/>
                      </linearGradient>
                    </defs>
                    <rect x="7" y="2" width="10" height="20" rx="2"/>
                    <line x1="11" y1="5" x2="13" y2="5"/>
                  </svg>
                  <h3>Segment</h3>
                  <div className="section-line green"></div>
                </div>
                <div className="premium-option-grid">
                  {[...new Set([form.segment, ...segments].filter(Boolean))].map((s) => (
                    <button 
                      key={s} 
                      type="button"
                      className={`premium-option-card ${form.segment === s ? 'active' : ''}`}
                      onClick={() => update("segment", s)}
                    >
                      <div className="option-icon-bubble">
                        {SegmentIcon(s)}
                      </div>
                      <span className="option-label">{capitalize(s)}</span>
                      <div className="option-check">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="premium-section-header compact">
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#groupGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="groupGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#a855f7"/>
                        <stop offset="100%" stopColor="#ec4899"/>
                      </linearGradient>
                    </defs>
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                  <h3>Gruppe</h3>
                  <div className="section-line purple"></div>
                </div>
                <div className="premium-option-grid">
                  {[...new Set([form.group, ...groups].filter(Boolean))].map((g) => (
                    <button 
                      key={g} 
                      type="button"
                      className={`premium-option-card ${form.group === g ? 'active' : ''}`}
                      onClick={() => update("group", g)}
                    >
                      <div className="option-icon-bubble">
                        {GroupIcon(g)}
                      </div>
                      <span className="option-label">{capitalize(g)}</span>
                      <div className="option-check">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="premium-section-header compact">
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#catLabel)" strokeWidth="2">
                    <defs>
                      <linearGradient id="catLabel" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f59e0b"/>
                        <stop offset="100%" stopColor="#ef4444"/>
                      </linearGradient>
                    </defs>
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                    <line x1="7" y1="7" x2="7.01" y2="7"/>
                  </svg>
                  <h3>Kategorie</h3>
                  <div className="section-line orange"></div>
                </div>
                <div className="premium-option-grid">
                  {categories.map((c) => (
                    <button 
                      key={c} 
                      type="button"
                      className={`premium-option-card ${!categoryNewMode && form.category === c ? 'active' : ''}`}
                      onClick={() => { update("category", c); setCategoryNewMode(false); }}
                    >
                      <div className="option-icon-bubble">
                        {CategoryIcon(c)}
                      </div>
                      <span className="option-label">{capitalize(c)}</span>
                      <div className="option-check">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                    </button>
                  ))}
                  <button 
                    type="button"
                    className={`premium-option-card add-new ${categoryNewMode ? 'active' : ''}`}
                    onClick={() => { setCategoryNewMode(true); if (!form.category && !categoryNew) setCategoryNew(""); }}
                  >
                    <div className="option-icon-bubble add-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 8v8m-4-4h8"/>
                      </svg>
                    </div>
                    <span className="option-label">Neue...</span>
                  </button>
                </div>
                {categoryNewMode && (
                  <div className="premium-input-container floating" style={{marginTop: '16px'}}>
                    <div className="input-icon-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14"/><path d="M5 12h14"/>
                      </svg>
                    </div>
                    <input 
                      value={categoryNew} 
                      onChange={(e) => setCategoryNew(e.target.value)} 
                      placeholder=" "
                      className="premium-input"
                    />
                    <label className="premium-label">Neue Kategorie</label>
                    <div className="input-glow"></div>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Features */}
            {activeTab === 'features' && (
              <div className="edit-lightbox-tab-content premium-content">
                <div className="premium-section-header compact">
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#featureGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="featureGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#00d4ff"/>
                        <stop offset="100%" stopColor="#a855f7"/>
                      </linearGradient>
                    </defs>
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <h3>Vordefinierte Features</h3>
                  <div className="section-line"></div>
                </div>
                <div className="premium-feature-grid">
                  {predefined.map((p) => {
                    const active = featuresList.includes(p.key);
                    return (
                      <button 
                        key={p.key} 
                        type="button" 
                        onClick={() => toggleFeature(p.key)} 
                        className={`premium-feature-chip ${active ? 'active' : ''}`}
                      >
                        <span className="feature-icon-wrap">{p.icon()}</span>
                        <span className="feature-text">{p.key}</span>
                        {active && (
                          <div className="feature-check-mark">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="premium-section-header compact" style={{marginTop: '16px'}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#addGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="addGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10b981"/>
                        <stop offset="100%" stopColor="#00d4ff"/>
                      </linearGradient>
                    </defs>
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 8v8m-4-4h8"/>
                  </svg>
                  <h3>Eigenes Feature</h3>
                  <div className="section-line green"></div>
                </div>
                <div className="premium-add-feature">
                  <div className="add-feature-input-wrap">
                    <input 
                      id="customFeatureInput" 
                      placeholder="Merkmal-Text eingeben..." 
                      className="premium-feature-input"
                    />
                    <button 
                      type="button"
                      className="add-feature-btn"
                      onClick={() => { 
                        const el = document.getElementById('customFeatureInput'); 
                        if (el) { addFeature(el.value); el.value = ''; } 
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14m-7-7h14"/>
                      </svg>
                      <span>Hinzufügen</span>
                    </button>
                  </div>
                </div>

                <div className="premium-section-header compact" style={{marginTop: '16px'}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#activeGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="activeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f59e0b"/>
                        <stop offset="100%" stopColor="#ef4444"/>
                      </linearGradient>
                    </defs>
                    <path d="M9 11l3 3L22 4"/>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                  </svg>
                  <h3>Aktive Features <span className="feature-count">{featuresList.length}</span></h3>
                  <div className="section-line orange"></div>
                </div>
                {featuresList.length === 0 ? (
                  <div className="premium-empty-state">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    <span>Keine Features ausgewählt</span>
                  </div>
                ) : (
                  <div className="premium-active-features">
                    {featuresList.map((f, idx) => (
                      <div key={f} className="active-feature-item" style={{animationDelay: `${idx * 0.05}s`}}>
                        <div className="feature-number">{idx + 1}</div>
                        <span className="feature-name">{f}</span>
                        <button type="button" onClick={() => removeFeature(f)} className="feature-delete-btn">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18m-2 0V20a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Design */}
            {activeTab === 'design' && (
              <div className="edit-lightbox-tab-content premium-content">
                <div className="premium-section-header compact">
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#designGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="designGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ec4899"/>
                        <stop offset="100%" stopColor="#a855f7"/>
                      </linearGradient>
                    </defs>
                    <circle cx="13.5" cy="6.5" r="0.5"/>
                    <circle cx="17.5" cy="10.5" r="0.5"/>
                    <circle cx="8.5" cy="7.5" r="0.5"/>
                    <circle cx="6.5" cy="12.5" r="0.5"/>
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z"/>
                  </svg>
                  <h3>Card-Stil</h3>
                  <div className="section-line pink"></div>
                </div>
                <div className="premium-design-grid">
                  <button 
                    type="button"
                    className={`premium-design-card ${form.cardStyle === 'standard' ? 'active' : ''}`}
                    onClick={() => update("cardStyle", "standard")}
                  >
                    <div className="design-preview standard-preview">
                      <div className="preview-line"></div>
                      <div className="preview-line short"></div>
                      <div className="preview-circle"></div>
                    </div>
                    <span className="design-label">Standard</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-design-card neon-card ${form.cardStyle === 'neon' ? 'active' : ''}`}
                    onClick={() => update("cardStyle", "neon")}
                  >
                    <div className="design-preview neon-preview">
                      <div className="preview-glow"></div>
                      <div className="preview-line"></div>
                      <div className="preview-line short"></div>
                    </div>
                    <span className="design-label">Neon Glow</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-design-card ${form.cardStyle === 'minimal' ? 'active' : ''}`}
                    onClick={() => update("cardStyle", "minimal")}
                  >
                    <div className="design-preview minimal-preview">
                      <div className="preview-dot"></div>
                      <div className="preview-line thin"></div>
                    </div>
                    <span className="design-label">Minimal</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-design-card dark-card ${form.cardStyle === 'dark' ? 'active' : ''}`}
                    onClick={() => update("cardStyle", "dark")}
                  >
                    <div className="design-preview dark-preview">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                      </svg>
                    </div>
                    <span className="design-label">Dark Mode</span>
                  </button>
                </div>

                <div className="premium-section-header compact">
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#iconGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#00d4ff"/>
                        <stop offset="100%" stopColor="#10b981"/>
                      </linearGradient>
                    </defs>
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <h3>Icon-Stil</h3>
                  <div className="section-line green"></div>
                </div>
                <div className="premium-icon-style-grid">
                  <button 
                    type="button"
                    className={`premium-icon-style ${form.iconStyle === 'filled' ? 'active' : ''}`}
                    onClick={() => update("iconStyle", "filled")}
                  >
                    <div className="icon-style-preview filled">
                      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
                        <circle cx="12" cy="12" r="8"/>
                      </svg>
                    </div>
                    <span>Gefüllt</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-icon-style ${form.iconStyle === 'outline' ? 'active' : ''}`}
                    onClick={() => update("iconStyle", "outline")}
                  >
                    <div className="icon-style-preview outline">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="8"/>
                      </svg>
                    </div>
                    <span>Umriss</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-icon-style ${form.iconStyle === 'gradient' ? 'active' : ''}`}
                    onClick={() => update("iconStyle", "gradient")}
                  >
                    <div className="icon-style-preview gradient">
                      <svg viewBox="0 0 24 24" fill="url(#iconPreviewGrad)" stroke="none">
                        <defs>
                          <linearGradient id="iconPreviewGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#00d4ff"/>
                            <stop offset="100%" stopColor="#a855f7"/>
                          </linearGradient>
                        </defs>
                        <circle cx="12" cy="12" r="8"/>
                      </svg>
                    </div>
                    <span>Gradient</span>
                  </button>
                </div>

                <div className="premium-section-header compact">
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#colorGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="colorGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#00d4ff"/>
                        <stop offset="33%" stopColor="#a855f7"/>
                        <stop offset="66%" stopColor="#ec4899"/>
                        <stop offset="100%" stopColor="#10b981"/>
                      </linearGradient>
                    </defs>
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="4"/>
                  </svg>
                  <h3>Farbschema</h3>
                  <div className="section-line rainbow"></div>
                </div>
                <div className="premium-color-grid">
                  <button 
                    type="button"
                    className={`premium-color-btn cyan ${form.colorScheme === 'cyan' ? 'active' : ''}`}
                    onClick={() => update("colorScheme", "cyan")}
                  >
                    <div className="color-bubble"></div>
                    <span>Cyan</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-color-btn purple ${form.colorScheme === 'purple' ? 'active' : ''}`}
                    onClick={() => update("colorScheme", "purple")}
                  >
                    <div className="color-bubble"></div>
                    <span>Purple</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-color-btn pink ${form.colorScheme === 'pink' ? 'active' : ''}`}
                    onClick={() => update("colorScheme", "pink")}
                  >
                    <div className="color-bubble"></div>
                    <span>Pink</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-color-btn green ${form.colorScheme === 'green' ? 'active' : ''}`}
                    onClick={() => update("colorScheme", "green")}
                  >
                    <div className="color-bubble"></div>
                    <span>Green</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-color-btn orange ${form.colorScheme === 'orange' ? 'active' : ''}`}
                    onClick={() => update("colorScheme", "orange")}
                  >
                    <div className="color-bubble"></div>
                    <span>Orange</span>
                  </button>
                  <button 
                    type="button"
                    className={`premium-color-btn custom ${form.colorScheme === 'custom' ? 'active' : ''}`}
                    onClick={() => { update("colorScheme", "custom"); setShowColorPicker(true); }}
                    style={{ '--custom-color': form.customColor }}
                  >
                    <div className="color-bubble" style={{ background: form.customColor }}></div>
                    <span>Custom</span>
                  </button>
                </div>
                
                {/* Custom Color Picker */}
                {showColorPicker && form.colorScheme === 'custom' && (
                  <div className="custom-color-picker">
                    <div className="color-picker-header">
                      <span>Eigene Farbe wählen</span>
                      <button type="button" onClick={() => setShowColorPicker(false)}>✕</button>
                    </div>
                    <div className="color-picker-body">
                      <div className="color-wheel-container">
                        <input 
                          type="color" 
                          value={form.customColor} 
                          onChange={(e) => { update("customColor", e.target.value); setColorInput(e.target.value); }}
                          className="color-wheel-input"
                        />
                        <div className="color-preview" style={{ background: form.customColor }}></div>
                      </div>
                      <div className="color-code-input">
                        <span>#</span>
                        <input 
                          type="text" 
                          value={colorInput.replace('#', '')} 
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                            setColorInput('#' + val);
                            if (val.length === 6) update("customColor", '#' + val);
                          }}
                          placeholder="00d4ff"
                          maxLength={6}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Icon-Auswahl Sektion */}
                <div className="premium-section-header compact" style={{ marginTop: '16px' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#iconSelectGradient)" strokeWidth="2">
                    <defs>
                      <linearGradient id="iconSelectGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f59e0b"/>
                        <stop offset="100%" stopColor="#ec4899"/>
                      </linearGradient>
                    </defs>
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M12 8v8M8 12h8"/>
                  </svg>
                  <h3>Card-Icon</h3>
                  <div className="section-line orange"></div>
                </div>
                <div className="premium-icon-select-grid">
                  <button 
                    type="button"
                    className={`premium-icon-select-btn ${!form.customIcon ? 'active' : ''}`}
                    onClick={() => update("customIcon", "")}
                  >
                    <span className="icon-none">—</span>
                    <span className="icon-label">Standard</span>
                  </button>
                  {availableIcons.map(ic => (
                    <button 
                      key={ic.id}
                      type="button"
                      className={`premium-icon-select-btn ${form.customIcon === ic.id ? 'active' : ''}`}
                      onClick={() => update("customIcon", ic.id)}
                    >
                      <span className="icon-emoji">{ic.icon}</span>
                      <span className="icon-label">{ic.label}</span>
                    </button>
                  ))}
                </div>

                <div className="premium-preview-card">
                  <div className="preview-header">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <span>Live-Vorschau</span>
                  </div>
                  <div className="preview-content">
                    <div className="preview-chip style">{form.cardStyle || 'Standard'}</div>
                    <div className="preview-chip icon">{form.iconStyle || 'Outline'}</div>
                    <div className="preview-chip color" data-color={form.colorScheme || 'cyan'}>{form.colorScheme || 'Cyan'}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Kosten-Übersicht - immer sichtbar am Ende */}
            {(form.price || form.promoPrice) && (
              <div className="cost-overview-box" style={{marginTop: '20px'}}>
                <div className="cost-overview-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                    <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                  Kostenübersicht
                </div>
                <div className="cost-overview-grid">
                  <div className="cost-item">
                    <span className="cost-label">Monatlich</span>
                    <span className="cost-value">{form.promoPrice || form.price} €</span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">Laufzeit</span>
                    <span className="cost-value">{form.termMonths || 24} Monate</span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">Effektivkosten</span>
                    <span className="cost-value highlight">
                      {(((parseFloat(form.promoPrice) || parseFloat(form.price) || 0) * (parseFloat(form.termMonths) || 24)) + (parseFloat(form.connectionFee) || 0)).toFixed(2)} €
                    </span>
                  </div>
                  {form.promoPrice && form.price && parseFloat(form.promoPrice) < parseFloat(form.price) && (
                    <div className="cost-item savings">
                      <span className="cost-label">Ersparnis</span>
                      <span className="cost-value">
                        {((parseFloat(form.price) - parseFloat(form.promoPrice)) * (parseFloat(form.termMonths) || 24)).toFixed(2)} € 
                        <small>({(((parseFloat(form.price) - parseFloat(form.promoPrice)) / parseFloat(form.price)) * 100).toFixed(0)}%)</small>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
          <div className="edit-lightbox-footer premium-footer">
            <div className="footer-glow"></div>
            <button type="button" className="premium-btn cancel" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6m0-6l6 6"/>
              </svg>
              <span>Abbrechen</span>
            </button>
            <button type="submit" className="premium-btn save">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
              <span>Speichern</span>
              <div className="btn-glow"></div>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
